import Fuse from "fuse.js";
import type { CommandGroupType } from "./command.types";

/**
 * Finds the parent node of a given command ID within a command structure.
 *
 * @format
 * @param {Array} commands - The list of command groups.
 * @param {string} commandId - The ID of the node whose parent needs to be found.
 * @returns {CommandGroupType|null} - The parent node object if found, otherwise null.
 */
export const findCommandParent = (
	commands: CommandGroupType[],
	commandId: string,
): CommandGroupType | null => {
	for (const group of commands) {
		if (!group.items?.length) continue;

		// direct child of this group
		if (group.items.some((item) => item.id === commandId)) {
			return group;
		}

		// recursive search inside children
		const nestedParent = findCommandParent(group.items, commandId);
		if (nestedParent) return nestedParent;
	}

	return null;
};

/**
 * Recursively finds a node by its ID within a nested command structure.
 *
 * @param {Array} commands - The list of command groups.
 * @param {string} commandId - The ID of the node to find.
 * @returns {CommandGroupType|null} - The node object if found, otherwise null.
 */
export const findCommandById = (
	commands: CommandGroupType[],
	commandId: string,
): CommandGroupType | null => {
	for (const group of commands) {
		if (group.id === commandId) return group;
		if (group.items) {
			for (const item of group.items) {
				if (item.id === commandId) return item;
				if (item.items) {
					const command = findCommandById(item.items, commandId);
					if (command) return command;
				}
			}
		}
	}
	return null;
};

/**
 * Recursively generating all of commands within a nested command structure.
 *
 * @param {Array} commands - The list of command groups.
 *
 * @returns {CommandGroupType|null} - The node object of all commands
 *
 */
export const flattenCommandGroups = (commands: CommandGroupType[]): any[] => {
	return commands.flatMap((group) =>
		(group.items || []).flatMap((item) => [
			{ ...item },
			...(item.items
				? flattenCommandGroups([
						{ id: `${item.id}_nested`, items: item.items },
					])
				: []),
		]),
	);
};

export const filterCommands = (
	value: string,
	search: string,
	keywords?: string[],
) => {
	if (!search) return 1;

	// Normalize strings for case-insensitive comparison
	const normalizedValue = value.toLowerCase();
	const normalizedSearch = search.toLowerCase();

	// Exact match gets highest priority
	if (normalizedValue === normalizedSearch) return 1;

	// Starts with match gets high priority
	if (normalizedValue.startsWith(normalizedSearch)) return 0.9;

	// Keywords exact match
	if (keywords?.some((k) => k.toLowerCase() === normalizedSearch)) return 0.8;

	// Contains match in main value
	if (normalizedValue.includes(normalizedSearch)) return 0.7;

	// Prepare data for fuzzy search
	const searchData = {
		title: value,
		keywords: keywords || [],
	};

	// Configure Fuse options
	const fuseOptions = {
		includeScore: true,
		keys: [
			{ name: "title", weight: 0.7 },
			{ name: "keywords", weight: 0.3 },
		],
		threshold: 0.4, // Lower threshold = stricter matching
		useExtendedSearch: true,
	};

	// Create Fuse instance for fuzzy search
	const fuse = new Fuse([searchData], fuseOptions);
	const result = fuse.search(normalizedSearch);

	if (result.length > 0) {
		// Fuse scores are 0-1 where 0 is a perfect match, convert to our system where 1 is perfect
		const fuseScore = 1 - (result[0].score || 0);

		// Scale to fit between 0.3-0.6 to position fuzzy matches below direct matches
		return 0.3 + fuseScore * 0.3;
	}

	// No match
	return 0;
};
